import {Component, EventEmitter, Input, Output} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {Router} from '@angular/router';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faArrowLeft, faTrash, faUpload} from '@fortawesome/free-solid-svg-icons';
import {FilesService} from '../../../../core/services/files.service';
import {FileModel, PendingFileUpload} from '../../../../core/models/file.model';
import {ErrorAlertComponent} from '../../../components/error-alert.component/error-alert.component';
import {getApiErrorMessage} from '../../../../core/utils/api-error-message';
import {MAX_FILE_UPLOAD_SIZE_BYTES} from '../../../../core/constants/file-upload.constants';

@Component({
  selector: 'app-create-files',
  imports: [ReactiveFormsModule, FaIconComponent, ErrorAlertComponent],
  templateUrl: './create-files.component.html',
  styleUrl: './create-files.component.css'
})
export class CreateFilesComponent {
  private static nextInputId = 0;

  @Input() inModal = false;
  @Input() orderId: string | null = null;
  @Input() deferUpload = false;
  @Output() completed = new EventEmitter<FileModel>();
  @Output() fileSelected = new EventEmitter<PendingFileUpload>();
  @Output() cancelled = new EventEmitter<void>();

  fileForm = new FormGroup({
    file: new FormControl<File | null>(null, Validators.required),
    description: new FormControl('', {
      nonNullable: true,
      validators: Validators.maxLength(1000)
    })
  });
  selectedFileName = '';
  error = '';
  uploading = false;
  isDragging = false;
  inputId = `file-${CreateFilesComponent.nextInputId++}`;
  descriptionInputId = `${this.inputId}-description`;
  faArrowLeft = faArrowLeft;
  faTrash = faTrash;
  faUpload = faUpload;

  constructor(private filesService: FilesService, private router: Router) {}

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const selectedFile = input.files?.[0] ?? null;

    this.handleFileSelection(selectedFile, input);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
    const selectedFile = event.dataTransfer?.files?.[0] ?? null;
    this.handleFileSelection(selectedFile);
  }

  submit(): void {
    if (this.fileForm.invalid) {
      this.error = 'Изберете датотека за прикачување.';
      return;
    }

    const file = this.fileForm.value.file;
    if (!file) {
      this.error = 'Изберете датотека за прикачување.';
      return;
    }

    const validationError = this.getFileValidationError(file);
    if (validationError) {
      this.error = validationError;
      this.resetFileSelection();
      return;
    }

    const description = this.fileForm.controls.description.value.trim();

    if (this.deferUpload) {
      this.fileSelected.emit({file, description});
      this.resetForm();
      return;
    }

    this.uploading = true;
    this.error = '';

    const upload = this.orderId
      ? this.filesService.uploadFileForOrder(this.orderId, file, description)
      : this.filesService.uploadFile(file, description);

    upload.subscribe({
      next: (storedFile) => {
        this.uploading = false;
        if (this.inModal) {
          this.completed.emit(storedFile);
          this.resetForm();
        } else {
          this.router.navigate(['/files']);
        }
      },
      error: (err) => {
        this.error = getApiErrorMessage(err, 'Датотеката не може да се прикачи.');
        console.error(err);
        this.uploading = false;
      }
    });
  }

  onCancel(): void {
    if (this.inModal) {
      this.cancelled.emit();
      this.resetForm();
    }
  }

  clearSelectedFile(): void {
    this.resetFileSelection();
  }

  private resetFileSelection(): void {
    this.selectedFileName = '';
    this.fileForm.patchValue({file: null});
    this.fileForm.markAsUntouched();
  }

  private resetForm(): void {
    this.fileForm.reset();
    this.resetFileSelection();
    this.error = '';
    this.uploading = false;
    this.isDragging = false;
  }

  private handleFileSelection(file: File | null, input?: HTMLInputElement): void {
    if (!file) {
      this.resetFileSelection();
      return;
    }

    const validationError = this.getFileValidationError(file);
    if (validationError) {
      this.error = validationError;
      if (input) input.value = '';
      this.resetFileSelection();
      return;
    }

    this.error = '';
    this.selectedFileName = file.name;
    this.fileForm.patchValue({file});
  }

  private getFileValidationError(file: File): string {
    if (file.size <= 0) {
      return 'Избраната датотека е празна.';
    }

    if (file.size > MAX_FILE_UPLOAD_SIZE_BYTES) {
      return 'Датотеката не смее да биде поголема од 200 MB.';
    }

    return '';
  }

}
